import { useTranslator } from '@eo-locale/react';
import dayjs from 'dayjs';
import { useNumberFormatter } from '@guest-widgets/shared/src/hooks/useNumberFormatter';

import type { Filters, DateRange, PriceRange, Location } from './filters';
import { Sort } from './filters';
import { useFilterAttributes } from './filterAttributesContext';
import { useSettings } from './settingsContext';

export enum FormatOption {
  FullContent,
  ShortLabel,
}

export const useFormatFilterValue = (): FormatFilterValue => {
  const { translate, formatDate, formatNumber } = useTranslator();
  const { formatKilometers } = useNumberFormatter();
  const { attributes } = useFilterAttributes();
  const { locale } = useSettings();

  const join = (a: string, b: string) =>
    translate('product-catalog.filters.from-to', { from: a, to: b });

  return {
    categories: (categories?: string[], formatOption?: FormatOption) => {
      if (!categories) return '';
      const amount = categories.length;

      if (amount === 1 || formatOption === FormatOption.FullContent) {
        return categories
          .map((id) => attributes?.categoryOptions?.find(({ value }) => value === id)?.title)
          .filter((c) => !!c)
          .join(', ');
      }

      if (formatOption === FormatOption.ShortLabel) {
        return translate('braces.label-number', {
          label: translate('product-catalog.filters.categories.title'),
          number: amount,
        });
      }

      return translate('product-catalog.filters.categories.chosen', {
        amount,
      });
    },

    dates: (dates?: DateRange, formatOption?: FormatOption) => {
      if (!dates) return '';
      const [from, to] = dates;
      const options: Intl.DateTimeFormatOptions = {
        numberingSystem: 'arab',
      };

      const formattedFrom = formatDate(from, locale === 'ar-AE' ? options : undefined);
      const formattedTo = formatDate(to, locale === 'ar-AE' ? options : undefined);

      if (from.getTime() === to.getTime()) return formattedFrom;
      if (formatOption !== FormatOption.ShortLabel) return join(formattedFrom, formattedTo);
      if (from.getFullYear() !== to.getFullYear()) return join(formattedFrom, formattedTo);
      if (from.getMonth() !== to.getMonth())
        return join(
          dayjs(from).format(translate('product-catalog.filters.date.day-month-mask')),
          formattedTo
        );
      return join(from.getDate().toString(), formattedTo);
    },

    location: (location?: Location) => {
      if (!location) return '';
      return `${location.name}, ${formatKilometers(location.radius)}`;
    },

    price: (price?: PriceRange) => {
      if (!price) return '';
      const [from, to] = price;
      const formattedPrice = (num: number) =>
        formatNumber(num, { style: 'currency', currency: attributes?.currency });
      const formattedFrom = formattedPrice(from);
      const formattedTo = formattedPrice(to);

      if (from === attributes?.maxPriceRange[0])
        return translate('product-catalog.filters.price.up-to', {
          price: formattedTo,
        });

      if (to === attributes?.maxPriceRange[1])
        return translate('product-catalog.filters.price.starting', {
          price: formattedFrom,
        });

      return join(formattedFrom, formattedTo);
    },

    sort: (sort?: Sort) => {
      if (!sort) return '';
      return translate(mapSortToMessageKey(sort));
    },

    misc: (topThingsToDo?: string[], formatOption?: FormatOption) => {
      if (!topThingsToDo) return '';

      if (topThingsToDo.length === 1 || formatOption === FormatOption.FullContent) {
        return topThingsToDo
          .map((id) => attributes?.miscOptions?.find(({ value }) => value === id)?.title)
          .filter((c) => !!c)
          .join(', ');
      }

      return translate('braces.label-number', {
        label: translate('product-catalog.filters.miscellaneous'),
        number: topThingsToDo.length,
      });
    },

    languages: (languages?: string[], formatOption?: FormatOption) => {
      if (!languages) return '';
      const amount = languages.length;
      if (amount === 1 || formatOption === FormatOption.FullContent) {
        return languages
          .map((id) => attributes?.availableLanguages?.find(({ value }) => value === id)?.title)
          .filter((c) => !!c)
          .join(', ');
      }
      if (formatOption == FormatOption.ShortLabel) {
        return translate('braces.label-number', {
          label: translate('product-catalog.filters.languages.title'),
          number: amount,
        });
      }
      return translate('product-catalog.filters.languages.chosen', {
        amount,
      });
    },
  };
};

export const mapSortToMessageKey = (sort: Sort) =>
  ({
    [Sort.LowestPrice]: 'product-catalog.filters.sorting.lowest-price',
    [Sort.HighestPrice]: 'product-catalog.filters.sorting.highest-price',
    [Sort.Distance]: 'product-catalog.filters.sorting.distance',
  }[sort]);

type multiSelection = 'dates' | 'categories' | 'misc' | 'languages';
type multiSelectionFilters = Required<Pick<Filters, multiSelection>>;
type uniSelectionFilters = Required<Omit<Filters, multiSelection>>;

type FormatFilterValue = {
  [key in keyof uniSelectionFilters]: (value: Filters[key]) => string;
} &
  {
    [key in keyof multiSelectionFilters]: (
      value: Filters[key],
      formatOption?: FormatOption
    ) => string;
  };
